import '@ui5/webcomponents/dist/features/InputSuggestions.js';
import {
  BusyIndicator,
  Input,
  InputDomRef,
  InputPropTypes,
  SuggestionItem,
  Ui5CustomEvent
} from '@ui5/webcomponents-react';
import type { InputSelectionChangeEventDetail } from '@ui5/webcomponents/dist/Input.js';
import { debounce } from 'lodash';
import { forwardRef, useRef, useState } from 'react';
import {
  MsGraphUser,
  useGetMsGraphUsersSearchByQueryQuery,
  useGetUserInfoQuery
} from 'generated/graphql';
import BusyIndicatorSize from '@ui5/webcomponents/dist/types/BusyIndicatorSize.js';

interface MsalEmailInputSearchProps extends InputPropTypes {
  onEmailChange: (value: string) => void;
  customSuggestionItemSelector?: (userItem: MsGraphUser) => string;
}

type ComboBoxEvent = Ui5CustomEvent<InputDomRef, never>;

const EMAIL_INPUT_DEBOUNCE_SEARCH_TIME = 600;

export const MsalEmailInputSearch = forwardRef<InputDomRef, MsalEmailInputSearchProps>(
  ({ value, onEmailChange, customSuggestionItemSelector, className, ...props }, ref) => {
    const [{ data: userData, fetching: fetchingUserData }] = useGetUserInfoQuery();

    const [isDebounceRunning, setIsDebounceRunning] = useState<boolean>(true);

    // Stores the resolved value from the last previewed suggestion item (selection-change).
    // In UI5 v2, keyboard selection (Enter) fires only `change`, not `selection-change`.
    // We use selection-change (arrow preview) to pre-resolve the value so onChange can use it.
    const pendingSelectedValue = useRef<string | null>(null);

    const isInternalUser = () => {
      return userData?.user?.internal ?? false;
    };

    const [{ data, fetching }] = useGetMsGraphUsersSearchByQueryQuery({
      pause:
        !value || value.length < 3 || fetchingUserData || !isInternalUser() || isDebounceRunning,
      variables: {
        query: value || ''
      }
    });

    const debouncedOnEmailChange = useRef(
      debounce((value: boolean) => {
        setIsDebounceRunning(value);
      }, EMAIL_INPUT_DEBOUNCE_SEARCH_TIME)
    ).current;

    const handleOnEmailChange = (event: ComboBoxEvent) => {
      setIsDebounceRunning(false);
      // In UI5 v2 keyboard selection fires only onChange (not onSelectionChange).
      // Use the pre-resolved value from the arrow-key preview if available.
      if (pendingSelectedValue.current !== null) {
        onEmailChange(pendingSelectedValue.current);
        pendingSelectedValue.current = null;
        return;
      }
      onEmailChange(event.target.value);
    };

    const handleOnEmailInput = (event: ComboBoxEvent) => {
      const { value } = event.target;
      pendingSelectedValue.current = null;
      onEmailChange(value);
      setIsDebounceRunning(true);
      debouncedOnEmailChange.cancel();

      if (!value) {
        onEmailChange('');
        return;
      }

      debouncedOnEmailChange(false);
    };

    const resolveValueFromItem = (item: any): string => {
      if (customSuggestionItemSelector) {
        const user = data?.users?.find((u) => u.id === item?.id);
        if (user) return customSuggestionItemSelector(user);
      }
      return item?.additionalText ?? '';
    };

    const handleOnSelectionChange = (
      event: Ui5CustomEvent<InputDomRef, InputSelectionChangeEventDetail>
    ) => {
      event.preventDefault();
      const item = event.detail.item as any;
      // Store the resolved value so onChange can use it when Enter is pressed.
      // Do NOT call onEmailChange here — that would commit the value on every arrow key preview.
      pendingSelectedValue.current = resolveValueFromItem(item);
    };

    return (
      <BusyIndicator
        active={fetching || fetchingUserData}
        delay={0}
        size={BusyIndicatorSize.S}
        className={className}
      >
        <Input
          ref={ref}
          data-testid="msal-email-input"
          value={value}
          className={className}
          onChange={handleOnEmailChange}
          onInput={handleOnEmailInput}
          onSelectionChange={handleOnSelectionChange}
          showSuggestions
          noTypeahead
          {...props}
        >
          {value &&
            data?.users?.map((user) => {
              const sapNumberTxt = user.sapNumber ? `(${user.sapNumber})` : '';

              return (
                <SuggestionItem
                  data-testid="msal-email-input-item"
                  id={user.id}
                  key={user.id}
                  text={`${user.displayName!} ${sapNumberTxt}`}
                  additionalText={user.userPrincipalName!}
                />
              );
            })}
        </Input>
      </BusyIndicator>
    );
  }
);
